import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import type { MultiFactorResolver, PhoneMultiFactorInfo } from '../../types/auth';
import { BaseModal } from '../common/UI/BaseModal';

interface MfaChallengeDialogProps {
  open: boolean;
  resolver: MultiFactorResolver | null;
  onComplete: (verificationCode: string) => Promise<void>;
  onCancel: () => void;
}

const MfaChallengeDialog: React.FC<MfaChallengeDialogProps> = ({
  open,
  resolver,
  onComplete,
  onCancel,
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  if (!open || !resolver) {
    return null;
  }

  const mfaHint = resolver.hints[0] as PhoneMultiFactorInfo; // Assuming phone is the only and first factor
  const phoneNumber = mfaHint.phoneNumber;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await onComplete(verificationCode);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Verification failed: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      title="Two-Step Verification"
      actions={
        <>
          <Button onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" form="mfa-form" variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : 'Verify'}
          </Button>
        </>
      }
    >
      <Box id="mfa-form" component="form" onSubmit={handleSubmit}>
        <Typography gutterBottom>
          For your security, we've sent a verification code to your phone number ending in ...
          {phoneNumber.slice(-4)}.
        </Typography>
        <Typography variant="body2" color="text.primary" gutterBottom>
          Enter the 6-digit code to complete your sign-in.
        </Typography>
        <TextField
          label="Verification Code"
          value={verificationCode}
          onChange={(e) => setVerificationCode(e.target.value.trim())}
          fullWidth
          required
          autoFocus
          sx={{ mt: 2 }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
    </BaseModal>
  );
};

export default MfaChallengeDialog;
